import { Box, Typography } from '@mui/material';

export const modernTheme = {
  colors: {
    primary: '#e31e24',
    secondary: '#059669',
    accent: '#7c3aed',
    warning: '#ea580c',
    error: '#dc2626',
    success: '#16a34a',
    info: '#0284c7',
    gray: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a'
    }
  },
  gradients: {
    primary: 'linear-gradient(135deg, #e31e24 0%, #c41920 100%)',
    secondary: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
    accent: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
    subtle: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    card: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32
  },
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24
  }
};

export const cardSx = {
  borderRadius: 3,
  border: `1px solid ${modernTheme.colors.gray[200]}`,
  boxShadow: modernTheme.shadows.md,
  background: modernTheme.gradients.card,
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: modernTheme.shadows.lg,
    transform: 'translateY(-2px)'
  }
};

export const iconBoxSx = (gradient, size = 48) => ({
  width: size,
  height: size,
  borderRadius: `${Math.round(size * 0.25)}px`,
  background: gradient,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: modernTheme.shadows.md,
  flexShrink: 0,
});

export const KPIProgressRing = ({ value, size = 110, thickness = 10, currentHours, targetHours }) => {
  const normalizedValue = Math.min(Math.max(value, 0), 100);
  const circumference = 2 * Math.PI * ((size - thickness) / 2);
  const strokeDashoffset = circumference - (normalizedValue / 100) * circumference;

  const getProgressColor = () => {
    if (normalizedValue >= 100) return '#16a34a';
    if (normalizedValue >= 75) return '#2563eb';
    if (normalizedValue >= 50) return '#ea580c';
    return '#dc2626';
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={(size - thickness) / 2}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={thickness}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={(size - thickness) / 2}
          fill="none"
          stroke={getProgressColor()}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{
            transition: 'stroke-dashoffset 1s ease-in-out, stroke 0.3s ease',
            filter: `drop-shadow(0 0 6px ${getProgressColor()}40)`
          }}
        />
      </svg>
      <Box
        sx={{
          top: 0, left: 0, bottom: 0, right: 0,
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 800, color: getProgressColor(), lineHeight: 1, mb: 0.5 }}
        >
          {Math.round(normalizedValue)}%
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: modernTheme.colors.gray[600], fontWeight: 600, fontSize: '0.7rem' }}
        >
          {formatHours(currentHours)}/{formatHours(targetHours, 0)}h
        </Typography>
      </Box>
    </Box>
  );
};

export const formatHours = (hours, decimals = 1) => {
  if (hours === null || hours === undefined) return '0';
  const num = Number(hours);
  if (isNaN(num)) return '0';
  return num % 1 === 0 ? num.toString() : num.toFixed(decimals);
};

// 'YYYY-MM' → 'T8/2026' (tháng kỷ lục GMV team của Lead)
export const monthLabel = (monthKey) => {
  if (!monthKey) return '';
  const [y, m] = String(monthKey).split('-');
  return `T${Number(m)}/${y}`;
};

export const formatMoney = (amount) => {
  if (amount === null || amount === undefined) return '0 ₫';
  const num = Number(amount);
  if (isNaN(num)) return '0 ₫';
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B ₫`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M ₫`;
  if (num >= 1000) return `${(num / 1000).toFixed(0)}K ₫`;
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency', currency: 'VND', maximumFractionDigits: 0
  }).format(num);
};

export const formatHoursMinutes = (record) => {
  if (record.minutes !== undefined) {
    const h = record.hours || 0;
    const m = record.minutes || 0;
    if (h > 0 && m > 0) return `${h}h${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }
  const totalHours = record.hours || 0;
  const h = Math.floor(totalHours);
  const m = Math.round((totalHours - h) * 60);
  if (h > 0 && m > 0) return `${h}h${m}m`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}m`;
  return `${totalHours}h`;
};

export const formatHoursMinutesFull = (record) => {
  if (record.minutes !== undefined) {
    const h = record.hours || 0;
    const m = record.minutes || 0;
    if (h > 0 && m > 0) return `${h} giờ ${m} phút`;
    if (h > 0) return `${h} giờ`;
    return `${m} phút`;
  }
  const totalHours = record.hours || 0;
  const h = Math.floor(totalHours);
  const m = Math.round((totalHours - h) * 60);
  if (h > 0 && m > 0) return `${h} giờ ${m} phút`;
  if (h > 0) return `${h} giờ`;
  if (m > 0) return `${m} phút`;
  return `${totalHours} giờ`;
};

export const calcTotalHours = (record) => {
  if (record.minutes !== undefined) {
    return (record.hours || 0) + ((record.minutes || 0) / 60);
  }
  return record.hours || 0;
};

export const calcEarning = (record, hourlyRate = 100000) => {
  const totalHours = calcTotalHours(record);
  return totalHours * hourlyRate;
};

export const formatEarningShort = (amount) => {
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency', currency: 'VND', maximumFractionDigits: 0
  }).format(amount);
};

export const getSmartGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Chào buổi sáng';
  if (hour < 18) return 'Chào buổi chiều';
  return 'Chào buổi tối';
};

export const Sparkline = ({ data = [], width = 80, height = 32, color = '#3b82f6' }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);

  const points = data.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height * 0.8) - height * 0.1;
    return `${x},${y}`;
  }).join(' ');

  const areaPoints = `0,${height} ${points} ${width},${height}`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`sparkGrad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#sparkGrad-${color.replace('#','')})`} />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={data.length > 0 ? (data.length - 1) * step : 0}
        cy={data.length > 0 ? height - ((data[data.length - 1] - min) / range) * (height * 0.8) - height * 0.1 : height / 2}
        r="3"
        fill={color}
      />
    </svg>
  );
};
